import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'

/**
 * The people on this merchant's account.
 *
 * Mirrors `/v1/team` in the API's `modules/team/routes.ts`. Members and pending
 * invites arrive together and are cached under one key, because every mutation
 * here moves both lists at once — accepting an invite removes a row from one
 * and adds it to the other, and two caches would show that as a person briefly
 * existing twice.
 */

export type TeamRole = 'owner' | 'staff' | 'read_only'

export const ROLE_LABEL: Record<TeamRole, string> = {
  owner: 'Owner',
  staff: 'Staff',
  read_only: 'Read only',
}

export interface TeamMember {
  id: string
  name: string
  email: string | null
  phone: string | null
  avatarUrl: string | null
  role: TeamRole
  /** So the list can refuse to offer "remove" against the person reading it. */
  isYou: boolean
  joinedAt: string
}

export interface TeamInvite {
  id: string
  name: string | null
  email: string | null
  role: TeamRole
  expired: boolean
  expiresAt: string
  createdAt: string
}

export interface Team {
  members: TeamMember[]
  invites: TeamInvite[]
}

/** What `POST /v1/team/invites` answers with. The URL is returned exactly once. */
export interface CreatedInvite {
  id: string
  role: TeamRole
  expiresAt: string
  url: string
}

const KEY = ['team']

export function useTeam() {
  const queryClient = useQueryClient()
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: KEY })

  const { data, isPending, error } = useQuery({
    queryKey: KEY,
    queryFn: () => api<Team>('/v1/team'),
  })

  const invite = useMutation({
    mutationFn: (input: { name?: string; email?: string; role: TeamRole }) =>
      api<CreatedInvite>('/v1/team/invites', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api<{ revoked: boolean }>(`/v1/team/invites/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: TeamRole }) =>
      api<{ id: string; role: TeamRole }>(`/v1/team/members/${id}`, { method: 'PATCH', body: { role } }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api<{ removed: boolean }>(`/v1/team/members/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  return { team: data, loading: isPending, error, invite, revoke, setRole, remove }
}
